import { useRef, useState } from "react";
import { AtSign, Check, FileText, Globe, Paperclip, Sparkles, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText, fileUrl, newKey } from "@/lib/api";
import { cn } from "@/lib/utils";

const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml,application/pdf";

export function Thumb({ u, size = "h-16 w-16", onRemove }) {
  const isDoc = u.kind === "document" || u.mime === "application/pdf";
  return (
    <div className={cn("relative shrink-0 rounded-xl overflow-hidden border border-border bg-white", size)} title={u.original_filename || u.name} data-testid={`upload-thumb-${u.id}`}>
      {u.thumb_url ? <img src={fileUrl(u.thumb_url)} alt={u.original_filename || u.name} className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-muted-foreground"><FileText className="h-5 w-5" /></div>}
      {isDoc && <span className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[9px] text-center py-0.5 uppercase tracking-wider">PDF</span>}
      {u.kind === "logo" && <span className="absolute top-0 inset-x-0 bg-gold text-black text-[9px] text-center py-0.5 uppercase tracking-wider">Logo</span>}
      {onRemove && <button type="button" onClick={onRemove} aria-label="Remove" className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center" data-testid={`upload-remove-${u.id}`}><X className="h-3 w-3" /></button>}
    </div>
  );
}

export function MaterialsCard({ materials, onState, onClose, compact }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const uploads = materials?.uploads || [];
  const pending = materials?.pending_count || 0;
  const cost = materials?.analysis_cost || 0;
  const room = (materials?.max_files || 10) - uploads.length;

  const upload = async (files) => {
    const list = Array.from(files || []).slice(0, Math.max(0, room));
    if (!list.length) return;
    const fd = new FormData();
    list.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const { data } = await api.post("/persona/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onState(data);
      toast.success(`${list.length} file${list.length === 1 ? "" : "s"} added`);
    } catch (e) { toast.error(errText(e)); } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const analyze = async () => {
    setAnalyzing(true);
    try { const { data } = await api.post("/persona/uploads/analyze", { idempotency_key: newKey() }); onState(data); } catch (e) { toast.error(errText(e)); } finally { setAnalyzing(false); }
  };
  const skip = async () => {
    try { const { data } = await api.post("/persona/uploads/skip"); onState(data); onClose?.(); } catch (e) { toast.error(errText(e)); }
  };
  const remove = async (id) => {
    try { const { data } = await api.delete(`/persona/uploads/${id}`); onState(data); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="mt-4 rounded-3xl border border-gold/40 surface p-4 sm:p-5 max-w-xl" data-testid="materials-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="eyebrow-label text-gold flex items-center gap-2"><Paperclip className="h-3.5 w-3.5" />Bring what you already have</span>
        {onClose && <button type="button" onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground" data-testid="materials-close"><X className="h-4 w-4" /></button>}
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        disabled={uploading || room <= 0}
        className={cn("w-full rounded-2xl border border-dashed px-4 py-6 text-center transition-colors", drag ? "border-gold bg-gold/10" : "border-border hover:border-gold/60", (uploading || room <= 0) && "opacity-60")}
        data-testid="materials-dropzone"
      >
        <Upload className="h-5 w-5 mx-auto text-gold mb-2" />
        <p className="text-sm">{uploading ? "Uploading…" : room <= 0 ? "That's the maximum — remove a file to add another" : "Drop files here or tap to choose"}</p>
        <p className="text-[11px] text-muted-foreground mt-1">Logo, photos, flyers, business cards, social graphics, brand guides · PNG, JPG, WEBP, SVG, PDF · several at once · up to {materials?.max_files || 10} files</p>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files)} data-testid="materials-input" />
      </button>
      {uploads.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="materials-uploads">
          {uploads.map((u) => <Thumb key={u.id} u={u} onRemove={!u.analyzed ? () => remove(u.id) : undefined} />)}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        {pending > 0 && (
          <button type="button" onClick={analyze} disabled={analyzing || materials?.analyzing} className="btn-gold h-9 px-4 text-xs sm:text-sm" data-testid="materials-analyze-button">
            <Sparkles className="h-3.5 w-3.5" />{materials?.analyzing ? "Reading your materials…" : `Analyze ${pending} file${pending === 1 ? "" : "s"} · ${cost} credits`}
          </button>
        )}
        {!compact && uploads.length === 0 && !materials?.website?.import && <button type="button" onClick={skip} className="btn-quiet h-9 text-xs" data-testid="materials-skip-button">I don't have any yet</button>}
        {pending > 0 && <span className="text-[11px] text-muted-foreground">{materials?.cost_per_file} credits per file — covers reading colours, type, logo and text</span>}
      </div>
      <WebsiteRow materials={materials} onState={onState} />
      <SocialRow onState={onState} />
    </div>
  );
}

function WebsiteRow({ materials, onState }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const imp = materials?.website?.import;
  const importing = materials?.website?.importing;
  const go = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/persona/website", { url: url.trim(), idempotency_key: newKey() }); onState(data); setUrl(""); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid="website-row">
      <div className="flex items-center gap-2 mb-2"><Globe className="h-3.5 w-3.5 text-gold" /><span className="text-xs font-medium">Already have a website?</span></div>
      {imp ? (
        <p className="text-xs text-muted-foreground" data-testid="website-imported">Read {imp.pages?.length} page{imp.pages?.length === 1 ? "" : "s"} from <span className="text-foreground">{imp.url}</span>. Paste another address to read a different site.</p>
      ) : null}
      <div className="flex items-center gap-2 mt-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }} placeholder="yourbusiness.com" className="flex-1 h-9 rounded-full border border-border bg-transparent px-3 text-sm focus:outline-none focus:border-gold/60" data-testid="website-url-input" />
        <button type="button" onClick={go} disabled={busy || importing || !url.trim()} className="btn-gold h-9 px-3 text-xs whitespace-nowrap" data-testid="website-analyze-button">{importing ? "Reading…" : `Read my site · ${materials?.website?.cost ?? 6} credits`}</button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">Up to 6 public pages — home, about, services, contact. Colours, logo, offers and words become suggestions you approve.</p>
    </div>
  );
}

const SOCIALS = ["instagram", "tiktok", "facebook", "linkedin", "youtube"];

function SocialRow({ onState }) {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({});
  const [saved, setSaved] = useState(false);
  const save = async () => {
    try { await api.post("/org/social", vals); setSaved(true); toast.success("Handles saved — add screenshots of your profile or top posts above and I'll read them"); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid="social-row">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-xs font-medium" data-testid="social-toggle"><AtSign className="h-3.5 w-3.5 text-gold" />Social profiles <span className="text-muted-foreground font-normal">· optional</span></button>
      {open && (
        <div className="mt-2 grid gap-2">
          {SOCIALS.map((k) => <input key={k} value={vals[k] || ""} onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))} placeholder={`${k[0].toUpperCase()}${k.slice(1)} handle or link`} className="h-9 rounded-full border border-border bg-transparent px-3 text-sm focus:outline-none focus:border-gold/60" data-testid={`social-${k}`} />)}
          <div className="flex items-center gap-2">
            <button type="button" onClick={save} className="btn-outline h-8 px-3 text-xs" data-testid="social-save">{saved ? "Saved" : "Save handles"}</button>
            <span className="text-[11px] text-muted-foreground">We don't read social platforms directly — want to add screenshots of your profile or top posts? Drop them above.</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function WebsiteFound({ found, messageId, onState }) {
  const [busy, setBusy] = useState(false);
  if (!found) return null;
  const decide = async (confirm) => {
    setBusy(true);
    try { const { data } = await api.post("/persona/website/testimonials", { message_id: messageId, confirm }); onState(data); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2" data-testid="website-found">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border surface px-3 py-2">
        <Globe className="h-4 w-4 text-gold" />
        {found.logo_thumb_url && <img src={fileUrl(found.logo_thumb_url)} alt="Logo from website" className="h-9 w-9 rounded-lg object-contain bg-white border border-border" />}
        {found.colors?.length > 0 && <div className="flex items-center gap-1" data-testid="website-found-colors">{found.colors.map((hex) => <span key={hex} title={hex} className="h-6 w-6 rounded-full border border-border" style={{ background: hex }} />)}</div>}
        {found.fonts?.filter(Boolean).length > 0 && <span className="text-xs text-muted-foreground">{found.fonts.filter(Boolean).join(" · ")}</span>}
        <span className="text-[11px] text-muted-foreground ml-auto">{found.pages} page{found.pages === 1 ? "" : "s"} read</span>
      </div>
      {found.testimonials_found?.length > 0 && (
        <div className="rounded-2xl border border-gold/40 surface px-4 py-3" data-testid="testimonials-confirm">
          <p className="text-xs font-medium mb-2">These look like reviews on your site:</p>
          <ul className="space-y-1 mb-3">{found.testimonials_found.slice(0, 3).map((t, i) => <li key={i} className="text-xs text-muted-foreground italic">“{t.quote}”{t.attribution ? ` — ${t.attribution}` : ""}</li>)}</ul>
          {found.testimonials_decision ? (
            <p className="text-xs text-gold" data-testid="testimonials-decision">{found.testimonials_decision === "confirmed" ? "Added as a testimonials suggestion above — accept, edit or skip it." : "Left out — nothing was added."}</p>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Are they real, and do you have permission to use them?</span>
              <button type="button" disabled={busy} onClick={() => decide(true)} className="btn-outline h-8 px-3 text-xs" data-testid="testimonials-yes">Yes, propose as testimonials</button>
              <button type="button" disabled={busy} onClick={() => decide(false)} className="btn-quiet h-8 text-xs" data-testid="testimonials-no">No, leave them out</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MaterialsFound({ found }) {
  if (!found) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 rounded-2xl border border-border surface px-3 py-2" data-testid="materials-found">
      {found.logo_thumb_url && <img src={fileUrl(found.logo_thumb_url)} alt="Detected logo" className="h-9 w-9 rounded-lg object-contain bg-white border border-border" data-testid="materials-found-logo" />}
      {found.colors?.length > 0 && <div className="flex items-center gap-1" data-testid="materials-found-colors">{found.colors.map((hex) => <span key={hex} title={hex} className="h-6 w-6 rounded-full border border-border" style={{ background: hex }} />)}</div>}
      {found.fonts?.length > 0 && <span className="text-xs text-muted-foreground" data-testid="materials-found-fonts">{found.fonts.filter(Boolean).join(" · ")}</span>}
      <span className="text-[11px] text-muted-foreground ml-auto">{found.files} file{found.files === 1 ? "" : "s"} read</span>
    </div>
  );
}

export function DirectionChoice({ value, onChange }) {
  const opts = [
    { id: "keep", title: "Keep my current look and refine it", body: "Your logo and the colours we found stay locked; type and imagery are tuned to match." },
    { id: "fresh", title: "Give me a fresh direction", body: "A new palette and type pairing — your uploaded logo stays available in Logo options." },
  ];
  return (
    <div className="grid gap-2 w-full max-w-md" role="radiogroup" data-testid="direction-choice">
      {opts.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={value === o.id} onClick={() => onChange(o.id)} className={cn("text-left rounded-2xl border px-4 py-3 transition-colors", value === o.id ? "border-gold bg-gold/10" : "border-border hover:border-foreground/30")} data-testid={`direction-${o.id}`}>
          <div className="flex items-center gap-2 text-sm font-medium">{value === o.id && <Check className="h-3.5 w-3.5 text-gold" />}{o.title}</div>
          <p className="text-xs text-muted-foreground mt-1">{o.body}</p>
        </button>
      ))}
    </div>
  );
}
